var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { antdMessage } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import Button from '@hellodarwin/core/lib/components/common/button';
import FormLabel from '@hellodarwin/core/lib/components/common/form-label';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import UploadIcon from '@hellodarwin/icons/dist/icons/Upload';
import { Form, Input, Modal, Upload } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import GinStepUploadFormRelatedFiles from './gin-step-upload-form-related-files';
import SelectedAssetCard from './selected-asset-card';
import { AssetDropZonePrompt, BucketAssetFields, BucketFormActions, EditFileNameFieldGroup, EditFileNameModalFields, EditableFileNameField, EditableFileNamesSection, ModalButtonRow, } from './styles';
const { Dragger } = Upload;
const getFileNameWithoutExtension = (fileName) => fileName.replace(/\.[^.]+$/, '');
const GinStepBucketAssetUploadForm = ({ handleAfterFinish: onAfterFinish, handleSaveAssets, assets, handleDownloadAsset, isDrawerOpen, isLoading, bucketName, fetchRelatedAssets, relatedFilesTitle, flattenRelatedFiles, }) => {
    const theme = useTheme();
    const { t } = useTranslations();
    const [form] = Form.useForm();
    const [newAssets, setNewAssets] = useState(undefined);
    const [oldAssets, setOldAssets] = useState(undefined);
    const [assetsToCopy, setAssetsToCopy] = useState(undefined);
    const [assetsToDelete, setAssetsToDelete] = useState(undefined);
    const [editingField, setEditingField] = useState(undefined);
    const [editingValue, setEditingValue] = useState('');
    useEffect(() => {
        setOldAssets(assets);
    }, [assets]);
    useEffect(() => {
        if (oldAssets) {
            form.setFieldsValue({
                old_display_names: Object.fromEntries(oldAssets.map((asset) => { var _a; return [asset.asset_id, (_a = asset.display_name) !== null && _a !== void 0 ? _a : '']; })),
            });
        }
    }, [oldAssets, form]);
    const resetLocalState = (nextOldAssets) => {
        setNewAssets(undefined);
        setOldAssets(nextOldAssets);
        setAssetsToCopy(undefined);
        setAssetsToDelete(undefined);
        setEditingField(undefined);
        setEditingValue('');
    };
    const hasAssetsToSave = !!(newAssets === null || newAssets === void 0 ? void 0 : newAssets.length) ||
        !!(oldAssets === null || oldAssets === void 0 ? void 0 : oldAssets.length) ||
        !!(assetsToCopy === null || assetsToCopy === void 0 ? void 0 : assetsToCopy.length) ||
        !!(assetsToDelete === null || assetsToDelete === void 0 ? void 0 : assetsToDelete.length);
    const initialValues = useMemo(() => {
        var _a;
        return ({
            bucket_name: bucketName !== null && bucketName !== void 0 ? bucketName : '',
            new_display_names: {},
            old_display_names: Object.fromEntries((_a = oldAssets === null || oldAssets === void 0 ? void 0 : oldAssets.map((asset) => { var _a; return [asset.asset_id, (_a = asset.display_name) !== null && _a !== void 0 ? _a : '']; })) !== null && _a !== void 0 ? _a : []),
            assets_to_copy_display_names: {},
        });
    }, [bucketName, oldAssets]);
    useEffect(() => {
        if (!isDrawerOpen) {
            resetLocalState(assets);
        }
        form.resetFields();
    }, [isDrawerOpen, form, assets]);
    const afterFinish = () => {
        form.resetFields();
        resetLocalState();
        onAfterFinish();
    };
    const handleSubmit = async () => {
        var _a, _b, _c;
        try {
            const values = await form.validateFields();
            if (!hasAssetsToSave) {
                console.error('No files to save. Please select at least one file.');
                antdMessage.error('No files to save. Please select at least one file.');
                return;
            }
            await handleSaveAssets({
                bucket_name: values.bucket_name,
                new_files: newAssets || [],
                new_display_names: (_a = values.new_display_names) !== null && _a !== void 0 ? _a : {},
                old_files: oldAssets || [],
                old_display_names: (_b = values.old_display_names) !== null && _b !== void 0 ? _b : {},
                assets_to_copy: assetsToCopy || [],
                assets_to_copy_display_names: (_c = values.assets_to_copy_display_names) !== null && _c !== void 0 ? _c : {},
                assets_to_link: [],
                assets_to_link_display_names: {},
                assets_to_delete: assetsToDelete || [],
            });
            afterFinish();
        }
        catch (info) {
            console.error('Form validation failed:', info);
        }
    };
    const beforeUpload = (assetFile) => {
        var _a;
        const currentNewDisplayNames = (_a = form.getFieldValue('new_display_names')) !== null && _a !== void 0 ? _a : {};
        form.setFieldsValue({
            new_display_names: Object.assign(Object.assign({}, currentNewDisplayNames), { [assetFile.uid]: getFileNameWithoutExtension(assetFile.name) }),
        });
        setNewAssets((prev) => [...(prev !== null && prev !== void 0 ? prev : []), assetFile]);
        return false;
    };
    const onRemoveNewAsset = (assetUid) => {
        var _a;
        const currentNewDisplayNames = (_a = form.getFieldValue('new_display_names')) !== null && _a !== void 0 ? _a : {};
        const _b = currentNewDisplayNames, _c = assetUid, _removedDisplayName = _b[_c], remaining = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
        form.setFieldsValue({ new_display_names: remaining });
        setNewAssets((prev) => prev === null || prev === void 0 ? void 0 : prev.filter((a) => a.uid !== assetUid));
    };
    const onRemoveOldAsset = (assetId) => {
        setOldAssets(oldAssets === null || oldAssets === void 0 ? void 0 : oldAssets.filter((a) => a.asset_id !== assetId));
        setAssetsToDelete((prev) => [...(prev !== null && prev !== void 0 ? prev : []), assetId]);
    };
    const onCopyAsset = (asset) => {
        var _a, _b;
        if (assetsToCopy === null || assetsToCopy === void 0 ? void 0 : assetsToCopy.some(({ asset_id }) => asset_id === asset.asset_id)) {
            return;
        }
        const currentCopiedDisplayNames = (_a = form.getFieldValue('assets_to_copy_display_names')) !== null && _a !== void 0 ? _a : {};
        setAssetsToCopy((prev) => (prev === null || prev === void 0 ? void 0 : prev.some(({ asset_id }) => asset_id === asset.asset_id))
            ? prev
            : [...(prev !== null && prev !== void 0 ? prev : []), asset]);
        form.setFieldsValue({
            assets_to_copy_display_names: Object.assign(Object.assign({}, currentCopiedDisplayNames), { [asset.asset_id]: (_b = asset.display_name) !== null && _b !== void 0 ? _b : '' }),
        });
    };
    const onRemoveCopyAsset = (assetId) => {
        var _a;
        setAssetsToCopy((prev) => prev === null || prev === void 0 ? void 0 : prev.filter((a) => a.asset_id !== assetId));
        const currentCopiedDisplayNames = (_a = form.getFieldValue('assets_to_copy_display_names')) !== null && _a !== void 0 ? _a : {};
        const _b = currentCopiedDisplayNames, _c = assetId, _removedDisplayName = _b[_c], remaining = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
        form.setFieldsValue({ assets_to_copy_display_names: remaining });
    };
    const openEditModal = (field, key, originalName, fallbackValue) => {
        var _a, _b;
        setEditingField({ field, key, originalName });
        setEditingValue((_b = (_a = form.getFieldValue([field, key])) !== null && _a !== void 0 ? _a : fallbackValue) !== null && _b !== void 0 ? _b : originalName);
    };
    const closeEditModal = () => {
        setEditingField(undefined);
        setEditingValue('');
    };
    const saveEditedName = () => {
        if (!editingField) {
            return;
        }
        const trimmedValue = editingValue.trim();
        if (!trimmedValue) {
            antdMessage.error(t('common|fileActions.fileNameRequired'));
            return;
        }
        form.setFieldValue([editingField.field, editingField.key], trimmedValue);
        closeEditModal();
    };
    const fileNameRequiredRules = [
        {
            required: true,
            message: t('common|fileActions.fileNameRequired'),
        },
    ];
    const existingAssetName = (asset) => asset.file_name || asset.display_name || '';
    const editableFileNameEntries = [
        ...(oldAssets !== null && oldAssets !== void 0 ? oldAssets : []).map((asset) => {
            var _a, _b, _c;
            return ({
                key: asset.asset_id,
                field: 'old_display_names',
                originalName: existingAssetName(asset),
                displayName: (_b = (_a = form.getFieldValue(['old_display_names', asset.asset_id])) !== null && _a !== void 0 ? _a : asset.display_name) !== null && _b !== void 0 ? _b : '',
                fallbackValue: (_c = asset.display_name) !== null && _c !== void 0 ? _c : '',
                onDownload: handleDownloadAsset
                    ? () => handleDownloadAsset(asset, undefined)
                    : undefined,
                onRemove: () => onRemoveOldAsset(asset.asset_id),
            });
        }),
        ...(assetsToCopy !== null && assetsToCopy !== void 0 ? assetsToCopy : []).map((asset) => {
            var _a, _b, _c;
            return ({
                key: asset.asset_id,
                field: 'assets_to_copy_display_names',
                originalName: existingAssetName(asset),
                displayName: (_b = (_a = form.getFieldValue(['assets_to_copy_display_names', asset.asset_id])) !== null && _a !== void 0 ? _a : asset.display_name) !== null && _b !== void 0 ? _b : '',
                fallbackValue: (_c = asset.display_name) !== null && _c !== void 0 ? _c : '',
                onDownload: handleDownloadAsset
                    ? () => handleDownloadAsset(asset, undefined)
                    : undefined,
                onRemove: () => onRemoveCopyAsset(asset.asset_id),
            });
        }),
        ...(newAssets !== null && newAssets !== void 0 ? newAssets : []).map((asset) => {
            var _a;
            return ({
                key: asset.uid,
                field: 'new_display_names',
                originalName: asset.name,
                displayName: (_a = form.getFieldValue(['new_display_names', asset.uid])) !== null && _a !== void 0 ? _a : getFileNameWithoutExtension(asset.name),
                fallbackValue: getFileNameWithoutExtension(asset.name),
                onDownload: handleDownloadAsset
                    ? () => handleDownloadAsset(undefined, asset)
                    : undefined,
                onRemove: () => onRemoveNewAsset(asset.uid),
            });
        }),
    ];
    return (_jsxs(Form, { layout: "vertical", form: form, initialValues: initialValues, children: [_jsxs(BucketAssetFields, { children: [_jsx(Form.Item, { name: 'bucket_name', label: t('assetsUpload|bucket_name'), rules: [
                            {
                                required: true,
                                message: t('assetsUpload|bucket_name_required'),
                            },
                        ], style: { width: '100%' }, children: _jsx(Input, {}) }), _jsx(Form.Item, { required: true, name: 'asset', style: { width: '100%' }, label: _jsx(FormLabel, { label: t('assetsUpload|file_assets') }), children: _jsx(Dragger, { name: "file", beforeUpload: beforeUpload, onRemove: (file) => onRemoveNewAsset(file.uid), fileList: [], pastable: true, multiple: true, disabled: isLoading, children: _jsxs(AssetDropZonePrompt, { children: [_jsx(UploadIcon, {}), _jsx(Typography, { elementTheme: "body2", children: t('assetsUpload|multiple_file_asset_drag_and_drop') })] }) }) }), editableFileNameEntries.length > 0 && (_jsxs(EditableFileNamesSection, { children: [_jsx(FormLabel, { label: t('common|selectedFiles', {
                                    count: editableFileNameEntries.length,
                                }) }), editableFileNameEntries.map((entry) => (_jsxs(EditableFileNameField, { children: [_jsx(Form.Item, { hidden: true, name: [entry.field, entry.key], initialValue: entry.fallbackValue, rules: fileNameRequiredRules, children: _jsx(Input, {}) }), _jsx(SelectedAssetCard, { originalName: entry.originalName, displayName: entry.displayName, disabled: isLoading, onDownload: entry.onDownload, onRemove: entry.onRemove, onEdit: () => openEditModal(entry.field, entry.key, entry.originalName, entry.fallbackValue) })] }, entry.key)))] }))] }), _jsxs(BucketFormActions, { children: [_jsx(Button, { size: "small", defaultStyle: theme.colors.white_1, disabled: isLoading, onClick: afterFinish, children: t('button|cancel') }), _jsx(Button, { size: "small", defaultStyle: theme.colors.primary, loading: isLoading, disabled: isLoading, onClick: handleSubmit, children: t('button|save') })] }), _jsx(GinStepUploadFormRelatedFiles, { assets: [...(oldAssets !== null && oldAssets !== void 0 ? oldAssets : []), ...(assetsToCopy !== null && assetsToCopy !== void 0 ? assetsToCopy : [])], handleCopyFile: onCopyAsset, fetchRelatedAssets: fetchRelatedAssets, title: relatedFilesTitle, flat: flattenRelatedFiles }), _jsx(Modal, { title: t('common|fileActions.editName'), open: !!editingField, onCancel: closeEditModal, footer: null, closable: !isLoading, maskClosable: !isLoading, children: _jsxs(EditFileNameModalFields, { children: [_jsxs(EditFileNameFieldGroup, { children: [_jsx(FormLabel, { label: t('common|fileActions.fileName') }), _jsx(Input, { value: editingValue, onChange: (event) => setEditingValue(event.target.value), disabled: isLoading })] }), _jsxs(ModalButtonRow, { children: [_jsx(Button, { size: "small", defaultStyle: theme.colors.white_1, disabled: isLoading, onClick: closeEditModal, children: t('button|cancel') }), _jsx(Button, { size: "small", defaultStyle: theme.colors.primary, disabled: isLoading, onClick: saveEditedName, children: t('button|save') })] })] }) })] }));
};
export default GinStepBucketAssetUploadForm;
